import {
  forwardRef,
  useImperativeHandle,
  useState,
  useEffect,
  useRef,
  MouseEvent,
  FocusEvent,
  KeyboardEvent
} from 'react'
import ReactDatePicker from 'react-datepicker'
import '../../../../../../node_modules/react-datepicker/dist/react-datepicker.css'

export const DTPicker = forwardRef<unknown, any>((props, ref) => {
  const [date, setDate] = useState<Date | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const datePickerRef = useRef<ReactDatePicker>(null)
  const lastZoom = useRef(window.devicePixelRatio)
  const isSelecting = useRef(false)

  const onDateChanged = (selectedDate: Date) => {
    isSelecting.current = true
    setDate(selectedDate)
    props.onDateChange(selectedDate)
    setIsOpen(false)

    // Allow the selection state to reset after the current event loop
    setTimeout(() => {
      isSelecting.current = false
    }, 0)
  }

  const handleInputClick = (e: MouseEvent) => {
    if (e && e.preventDefault) {
      e.preventDefault()
    }
    if (!isSelecting.current && !isOpen) {
      setIsOpen(true)
    }
  }

  const handleFocus = (e: FocusEvent) => {
    // Prevent focus during selection to avoid the flash
    if (isSelecting.current) {
      e.preventDefault()
      ;(e.target as HTMLInputElement).blur()
      return
    }

    if (!isOpen) {
      setIsOpen(true)
    }
  }

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'Tab') {
      e.preventDefault()
    }
  }

  // Add zoom detection to close calendar
  useEffect(() => {
    const handleZoomChange = () => {
      const currentZoom = window.devicePixelRatio

      if (currentZoom !== lastZoom.current && isOpen) {
        // Force close the calendar
        setIsOpen(false)

        // Also try to blur the input to ensure it's properly closed
        if (datePickerRef.current) {
          try {
            // Try to access the input element and blur it
            const inputElement = datePickerRef.current.input
            if (inputElement && typeof inputElement.blur === 'function') {
              inputElement.blur()
            }
          } catch (error) {
            console.warn('Error blurring DTPicker input:', error)
          }
        }

        // Force hide calendar elements with our specific popper class
        const calendarElements = document.querySelectorAll('.datepicker-filter-popper, .react-datepicker-popper')
        calendarElements.forEach((element) => {
          if (element instanceof HTMLElement) {
            element.style.display = 'none'
            element.style.visibility = 'hidden'
            element.style.opacity = '0'
          }
        })

        lastZoom.current = currentZoom
      }
    }

    const handleResize = () => {
      // Small delay to ensure zoom change is detected properly
      setTimeout(handleZoomChange, 50)
    }

    const handleVisualViewportChange = () => {
      handleZoomChange()
    }

    window.addEventListener('resize', handleResize)

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleVisualViewportChange)
    }

    return () => {
      window.removeEventListener('resize', handleResize)
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleVisualViewportChange)
      }
    }
  }, [isOpen]) // Depend on isOpen so we only close when calendar is actually open

  useImperativeHandle(ref, () => ({
    getDate() {
      return date
    },

    setDate(date: Date | null) {
      setDate(date)
    },

    // Add method to programmatically close the calendar
    closeCalendar() {
      setIsOpen(false)
    }
  }))

  return (
    <ReactDatePicker
      ref={datePickerRef}
      portalId="root"
      placeholderText="MM/dd/yy"
      dateFormat="MM/dd/yy"
      popperClassName="ag-custom-component-popup datepicker-filter-popper"
      selected={date}
      onChange={onDateChanged as any}
      enableTabLoop
      strictParsing
      shouldCloseOnSelect={true}
      formatWeekDay={(day) => day.charAt(0)}
      showIcon={true}
      onKeyDown={handleKeyDown}
      autoComplete="off"
      open={isOpen}
      onCalendarOpen={() => setIsOpen(true)}
      onCalendarClose={() => setIsOpen(false)}
      onClickOutside={() => setIsOpen(false)}
      onInputClick={handleInputClick as any}
      onFocus={handleFocus}
      readOnly={isSelecting.current}
      icon={
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
          <path
            d="M5.0625 2.25024V3.93774M12.9375 2.25024V3.93774M2.25 14.0627V5.62524C2.25 4.69326 3.00552 3.93774 3.9375 3.93774H14.0625C14.9945 3.93774 15.75 4.69326 15.75 5.62524V14.0627M2.25 14.0627C2.25 14.9947 3.00552 15.7502 3.9375 15.7502H14.0625C14.9945 15.7502 15.75 14.9947 15.75 14.0627M2.25 14.0627V8.43774C2.25 7.50576 3.00552 6.75024 3.9375 6.75024H14.0625C14.9945 6.75024 15.75 7.50576 15.75 8.43774V14.0627"
            stroke="#FFA9FC"
            strokeWidth="1.25"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      }
    />
  )
})
